/**
 * 상담자/내담자 역할 자동 추론 (SPEAKER_ROLE_PROTOCOL v0.2 §4~§8).
 *
 *   1) Galaxy 턴을 문장 단위(unit)로 나누고 맞장구를 분리
 *   2) unit마다 담화 기능 특징 점수 (+: 상담자, −: 내담자)
 *   3) 기기 화자 라벨별 회기 전체 경향(prior) + 턴 순서 모델(2-state HMM, forward-backward) → 턴 posterior
 *   4) 턴 안에서 강하게 반대되는 unit은 분리(혼합 턴), 근거가 약하면 unknown/mixed
 *
 * 안전 원칙: 상담자 발화를 내담자로 확신하는 오류가 가장 위험하다.
 *   - client 판정은 counselor 판정보다 높은 근거를 요구한다.
 *   - 상담자 단서가 있는 unit은 client로 판정되어도 신뢰도를 낮춘다 (CLIENT_REPORT 자동 근거 불가).
 * 기기 라벨은 근거 중 하나일 뿐 그대로 역할로 쓰지 않는다. 사용자 확인을 요구하지 않는다.
 */
import type { RawTurn } from './parse';
import { contentTokens } from './normalize';

export const ROLE_MODEL_VERSION = 'rules-hmm-0.1';

export type Act =
  | 'question'
  | 'reflection'
  | 'interpretation'
  | 'self-disclosure'
  | 'validation'
  | 'structuring'
  | 'backchannel'
  | 'other';

export type InferredUnit = {
  turnIndex: number;
  unitIndex: number;
  charStart: number;
  charEnd: number;
  text: string;
  role: 'counselor' | 'client' | 'unknown' | 'mixed';
  confidence: number;
  isBackchannel: boolean;
  counselorAct?: Act;
  /** 상담자의 해석에 대한 짧은 동의 (T3) */
  weakAgreement: boolean;
  localCorrection: boolean;
  score: number;
};

type Feature = { re: RegExp; w: number; act?: Act; kind: 'C' | 'K' };

// ---- 상담자 단서 (+) ----
const C: Feature[] = [
  // C1 상대의 경험·상태를 묻는 질문 (존칭 + 의문)
  { re: /(셨|시|세|실|십|드셨|하셨|계셨|으셨)[^.?!]{0,12}\?$/, w: 2.2, act: 'question', kind: 'C' },
  { re: /(나요|까요|세요|신가요|셨어요|셨나요|으세요|실까요)\?$/, w: 1.2, act: 'question', kind: 'C' },
  // C2 재진술/반영
  {
    re: /(신 거네요|셨던 거네요|신 거죠|셨던 거죠|시는군요|셨군요|시네요|셨네요|계셨던 것 같아요|셨던 것 같아요|남으셨|붙들고 계셨)/,
    w: 2.2,
    act: 'reflection',
    kind: 'C',
  },
  { re: /(라고 볼 수 있을까요|이런 느낌이라고|라는 거죠\?|말씀이시죠)/, w: 1.6, act: 'reflection', kind: 'C' },
  // C3 타당화
  {
    re: /(쉬운 일이 아니|고마워요|감사하기도|느껴져요|대단한|잘하고 계|잘 하고 계|너무 좋습니다|애쓰셨)/,
    w: 1.6,
    act: 'validation',
    kind: 'C',
  },
  // C4 구조화
  {
    re: /(오늘 나눈|이번 회기|다음 주에|다음 시간|정리해 볼|판단하지 않으니|편하게 말씀|이어서 이야기)/,
    w: 1.8,
    act: 'structuring',
    kind: 'C',
  },
  // C5 체크인
  { re: /(지내셨|어떠셨|보내셨|한 주 동안|지난번 뵙고)/, w: 1.5, act: 'question', kind: 'C' },
  // C6 완곡한 해석
  {
    re: /(아닐까 싶|아닐까,|그런 생각이 들어요|제가 듣기에|제가 보기에|어떻게 보면|중요한 지점)/,
    w: 1.6,
    act: 'interpretation',
    kind: 'C',
  },
  // C7 상담자 자기개방
  { re: /^저도\s.*(관심|비슷한|짐작|반가)/, w: 1.8, act: 'self-disclosure', kind: 'C' },
];

// ---- 내담자 단서 (−) ----
const K: Feature[] = [
  // K1 1인칭 경험 서술 (과거)
  {
    re: /(저는|제가|내가|나는|저희가|저한테)[^?]*(었어요|았어요|했어요|였어요|었는데|았는데|더라고요|었고|았고|었다)/,
    w: -1.6,
    kind: 'K',
  },
  // K2 정서 자기보고 (존칭 없이)
  {
    re: /(불안|무섭|무서웠|힘들었|서운|억울|답답|부끄러|외로|막막|초조|짜증|지쳤|죄책감|우울|피곤|무기력|허전|속상|위축|도망치고)[가-힣]*(어요|해요|요\.|던 것 같아요|들어요|들었어요)/,
    w: -1.5,
    kind: 'K',
  },
  // K3 응답 개시
  { re: /^(그러니까|그니까|어 |음 |사실은|솔직히|아 )/, w: -0.6, kind: 'K' },
  // K4 상담자 호칭·상담자 행위 존칭
  { re: /(선생님|전도사님|말씀하신 거|말씀하셨|말씀하셨던|들으실지|해 봤는데)/, w: -1.6, kind: 'K' },
  // K5 망설임
  { re: /(해도 될지 모르겠|어떻게 들으실지)/, w: -1.0, kind: 'K' },
  // K2b 정서어가 문장 안에 있고 존칭 없이 끝남 (예: 답답했고 그게 오래 갔어요)
  {
    re: /^(?!.*(셨|시는|시고|세요|으시|드셨|하셨))(?=.*(불안|무섭|힘들|서운|억울|답답|부끄러|외로|막막|초조|짜증|지쳤|죄책감|우울|피곤|무기력|허전|속상|위축))[^?]*요[.!]?$/,
    w: -1.2,
    kind: 'K',
  },
  // K8 1인칭 현재 진술
  {
    re: /^(?!.*(셨|시는|세요|으시|저도))[^?]*(저한테|저는|제가|저에게)[^?]*(것 같아요|거든요|해요|어요|아요)[.!]?$/,
    w: -1.1,
    kind: 'K',
  },
  // K6 존칭 없는 평서 (상대에 대한 존칭 표지 없음)
  { re: /^(?!.*(셨|시는|시고|세요|십|으시|드리))[^?]*요[.!]?$/, w: -0.4, kind: 'K' },
];

/** 과거형 평서 종결: 받침 ㅆ 음절 + 어요/고요 (했어요, 잤어요, 봤어요, 있었고요) */
export function endsWithPastDeclarative(text: string): boolean {
  const m = /([가-힣])(어요|고요)[.!]?$/.exec(text.trim());
  if (!m) return false;
  const code = m[1]!.charCodeAt(0) - 0xac00;
  return code >= 0 && code % 28 === 20; // 종성 ㅆ
}

const HONORIFIC_TO_OTHER = /(셨|시는|시고|세요|으시|계시|드셨|하셨)/;
/** 제3자 높임 (하나님께서 하시는, 교수님이 오셨다) — 듣는 사람에 대한 존칭이 아니다 */
const THIRD_PARTY_HONORIFIC = /(께서|님이|님은|님께|님도|하나님|주님|성령)/;
const FIRST_PERSON = /(저는|제가|내가|나는|저도)/;
const BACKCHANNEL = /^(네|네네|네 네|예|음|어|아|응|그렇죠|맞아요|그래요|그쵸)[.!?]?\s*$/;
const WEAK_AGREEMENT = /^(네|음|예)?\s*(그런 것 같기도 해요|그럴 수도 있겠네요|그런 것 같아요|그렇네요)[.!]?$|^네\.?$/;

/** 문장 경계로 unit 분리 (원문 위치 보존) */
export function splitUnits(text: string): { start: number; end: number; text: string }[] {
  const out: { start: number; end: number; text: string }[] = [];
  const re = /[^.?!]+[.?!]*|[.?!]+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const raw = m[0];
    const lead = raw.length - raw.trimStart().length;
    const t = raw.trim();
    if (!t) continue;
    out.push({ start: m.index + lead, end: m.index + lead + t.length, text: t });
  }
  return out;
}

export function scoreUnit(text: string): { score: number; act?: Act; counselorCue: boolean; clientCue: boolean } {
  let score = 0;
  let act: Act | undefined;
  let bestC = 0;
  let counselorCue = false;
  for (const f of C) {
    if (f.re.test(text)) {
      score += f.w;
      counselorCue = true;
      if (f.w > bestC) {
        bestC = f.w;
        act = f.act;
      }
    }
  }
  let clientScore = 0;
  let strongClient = false;
  for (const f of K) {
    if (f.re.test(text)) {
      clientScore += f.w;
      if (f.w <= -1.0) strongClient = true; // K1·K2·K4·K5 (응답 개시어·평서 종결만으로는 약함)
    }
  }
  // K7 존칭 없는 과거 사건 서술 (주어가 생략된 자기보고: ~일이 있었어요, 잠을 못 잤어요)
  if (endsWithPastDeclarative(text) && !/(셨|시는|시고|세요|으시|드셨|하셨|저도)/.test(text)) {
    clientScore += -1.2;
    strongClient = true;
  }
  // T1: 상담자 해석·자기개방·질문이 있으면 1인칭이어도 내담자 점수를 주지 않는다
  const suppress =
    counselorCue &&
    (act === 'interpretation' || act === 'self-disclosure' || act === 'question' || act === 'reflection');
  if (!suppress) score += clientScore;
  // 상대 행위에 대한 존칭이 있고 1인칭 경험 서술이 아니면 약한 상담자 쪽
  const honorificToOther =
    HONORIFIC_TO_OTHER.test(text) &&
    !FIRST_PERSON.test(text) &&
    !/말씀하|들으실/.test(text) &&
    !THIRD_PARTY_HONORIFIC.test(text);
  if (honorificToOther) score += 0.6;
  return {
    score,
    ...(act ? { act } : {}),
    // 상대에 대한 존칭도 약한 상담자 단서로 본다 (client 확신을 막는 안전장치)
    counselorCue: counselorCue || honorificToOther,
    clientCue: strongClient && !suppress,
  };
}

const sigmoid = (x: number) => 1 / (1 + Math.exp(-x));
const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

export type RoleOptions = { switchProb?: number; priorWeight?: number };

export function inferRoles(turns: RawTurn[], opts: RoleOptions = {}): InferredUnit[] {
  const pSwitch = opts.switchProb ?? 0.75;
  const priorW = opts.priorWeight ?? 0.8;

  // 1) unit 분해 + 점수
  const perTurn = turns.map((t) =>
    splitUnits(t.text).map((u, k) => {
      const isBackchannel = BACKCHANNEL.test(u.text) && contentTokens(u.text).length <= 2;
      const s = isBackchannel ? { score: 0, counselorCue: false, clientCue: false } : scoreUnit(u.text);
      return { ...u, unitIndex: k, isBackchannel, ...s };
    }),
  );
  const turnScore = perTurn.map((units) => {
    const sum = units.filter((u) => !u.isBackchannel).reduce((a, u) => a + u.score, 0);
    return clamp(sum, -6, 6);
  });

  // 2) 기기 라벨별 회기 전체 경향 (prior)
  const byLabel = new Map<string, number[]>();
  turns.forEach((t, i) => byLabel.set(t.label, [...(byLabel.get(t.label) ?? []), turnScore[i]!]));
  const labelPrior = new Map<string, number>();
  for (const [label, scores] of byLabel) {
    const mean = scores.reduce((a, b) => a + b, 0) / Math.max(1, scores.length);
    labelPrior.set(label, clamp(mean * 2, -2.5, 2.5));
  }

  // 3) HMM forward-backward (상태 0 = counselor, 1 = client)
  const n = turns.length;
  const emitC = turns.map((t, i) => sigmoid(turnScore[i]! + priorW * (labelPrior.get(t.label) ?? 0)));
  const T = [
    [1 - pSwitch, pSwitch],
    [pSwitch, 1 - pSwitch],
  ];
  const fwd: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const e: [number, number] = [emitC[i]!, 1 - emitC[i]!];
    let a: [number, number];
    if (i === 0) a = [0.5 * e[0], 0.5 * e[1]];
    else {
      const p = fwd[i - 1]!;
      a = [(p[0] * T[0]![0]! + p[1] * T[1]![0]!) * e[0], (p[0] * T[0]![1]! + p[1] * T[1]![1]!) * e[1]];
    }
    const z = a[0] + a[1] || 1;
    fwd.push([a[0] / z, a[1] / z]);
  }
  const bwd: [number, number][] = new Array(n);
  for (let i = n - 1; i >= 0; i--) {
    if (i === n - 1) {
      bwd[i] = [1, 1];
      continue;
    }
    const e: [number, number] = [emitC[i + 1]!, 1 - emitC[i + 1]!];
    const b = bwd[i + 1]!;
    const v: [number, number] = [
      T[0]![0]! * e[0] * b[0] + T[0]![1]! * e[1] * b[1],
      T[1]![0]! * e[0] * b[0] + T[1]![1]! * e[1] * b[1],
    ];
    const z = v[0] + v[1] || 1;
    bwd[i] = [v[0] / z, v[1] / z];
  }
  const postC = fwd.map((f, i) => {
    const a = f[0] * bwd[i]![0];
    const b = f[1] * bwd[i]![1];
    return a / (a + b || 1);
  });

  // 지역 swap 탐지: 라벨 경향과 반대로 판정된 턴
  const labelSide = (label: string) => ((labelPrior.get(label) ?? 0) >= 0 ? 'counselor' : 'client');

  // 4) unit 역할
  const out: InferredUnit[] = [];
  perTurn.forEach((units, i) => {
    const pc = postC[i]!;
    const turnRole: 'counselor' | 'client' = pc >= 0.5 ? 'counselor' : 'client';
    const turnConf = Math.max(pc, 1 - pc);
    const localCorrection = turnRole !== labelSide(turns[i]!.label) && turnConf >= 0.7;
    for (const u of units) {
      let role: InferredUnit['role'] = turnRole;
      let conf = turnConf;
      if (u.isBackchannel) {
        // 듣는 사람의 맞장구일 가능성이 크다: 임상 근거로 쓰지 않는다
        role = 'unknown';
        conf = 0.5;
      } else if (Math.abs(u.score) >= 1.5 && Math.sign(u.score) !== (turnRole === 'counselor' ? 1 : -1)) {
        // 턴 안에서 강하게 반대되는 unit: 혼합 턴 분할
        role = u.score > 0 ? 'counselor' : 'client';
        conf = Math.min(0.8, sigmoid(Math.abs(u.score)));
      } else if (u.score === 0 || Math.sign(u.score) !== (turnRole === 'counselor' ? 1 : -1)) {
        conf = turnConf - 0.15; // 이 unit 자체 근거가 없거나 약하게 반대
      }
      // 안전 규칙: 상담자 단서가 있는 unit은 client로 확신하지 않는다
      if (role === 'client' && u.counselorCue) conf = Math.min(conf, 0.6);
      // client는 자체 근거가 있을 때만 높은 신뢰도
      if (role === 'client' && !u.clientCue) conf = Math.min(conf, 0.8);
      conf = clamp(conf, 0, 0.97);
      if ((role === 'counselor' || role === 'client') && conf < 0.6) role = 'unknown';
      out.push({
        turnIndex: i,
        unitIndex: u.unitIndex,
        charStart: u.start,
        charEnd: u.end,
        text: u.text,
        role,
        confidence: Number(conf.toFixed(3)),
        isBackchannel: u.isBackchannel,
        ...(role === 'counselor' && u.act
          ? { counselorAct: u.act }
          : u.isBackchannel
            ? { counselorAct: 'backchannel' as const }
            : {}),
        weakAgreement: WEAK_AGREEMENT.test(u.text),
        localCorrection,
        score: Number(u.score.toFixed(2)),
      });
    }
  });
  return out;
}
